import type { HttpRequestInit, HttpResult } from '@core/utils/http';
import {
  OMS_COLUMN_LAYOUT_NAME,
  omsLayoutListSchema,
  omsLayoutSchema,
  type OmsFillGroupBy,
  type OmsLayout,
  type OmsLayoutColumn,
  type OmsLayoutList,
  type OmsLayoutSort,
} from '@hf/sharedContract';

const LAYOUT_PATH = `/oms/layouts/blotter/${OMS_COLUMN_LAYOUT_NAME}`;

type Request = <T>(path: string, init?: HttpRequestInit<unknown, T>) => Promise<HttpResult<T>>;

type Draft = {
  columns: OmsLayoutColumn[];
  sort?: OmsLayoutSort;
  fillGroupBy?: OmsFillGroupBy;
};

let version: number | undefined;
let draft: Draft = { columns: [] };
let hydrated = false;
let sending = false;
let pending = false;
let conflicts = 0;
let lastRequest: Request | undefined;
let load: Promise<OmsLayout | undefined> | null = null;
let listeners = 0;
const waiters: Array<(ok: boolean) => void> = [];

export function syncFillLayout(layout: OmsLayout | undefined): void {
  if (!layout) {
    if (!sending && !pending) {
      version = undefined;
      draft = { columns: [] };
    }
    hydrated = true;
    if (pending && lastRequest) void pump(lastRequest);
    return;
  }
  const nextVersion = typeof layout.version === 'number' ? layout.version : undefined;
  if (!sending && !pending) {
    version = nextVersion;
    draft = { columns: layout.columns ?? [], sort: layout.sort, fillGroupBy: layout.fillGroupBy };
  } else if (version == null && nextVersion != null) {
    version = nextVersion;
    if (draft.columns.length === 0 && layout.columns.length > 0) draft.columns = layout.columns;
  }
  hydrated = true;
  if (pending && lastRequest) void pump(lastRequest);
}

/** One GET for the Trade Blotter layout. Call `cancel` from the effect cleanup. */
export function loadFillLayout(request: Request): { result: Promise<OmsLayout | undefined>; cancel: () => void } {
  listeners += 1;
  if (!load) {
    const flight = request<OmsLayoutList>('/oms/layouts?tab=blotter', { responseSchema: omsLayoutListSchema })
      .then((result) => {
        const data = result.status === 200 ? result.data : undefined;
        if (!data || !('layouts' in data) || !Array.isArray(data.layouts)) return undefined;
        return data.layouts.find((item) => item.layoutName === OMS_COLUMN_LAYOUT_NAME);
      })
      .finally(() => {
        if (load === flight) load = null;
      });
    load = flight;
  }
  const flight = load;
  let released = false;
  return {
    result: flight,
    cancel() {
      if (released) return;
      released = true;
      listeners -= 1;
      if (listeners === 0 && load === flight) load = null;
    },
  };
}

/** Tests start from an empty layout. */
export function resetFillLayoutWrite(): void {
  version = undefined;
  draft = { columns: [] };
  hydrated = false;
  sending = false;
  pending = false;
  conflicts = 0;
  lastRequest = undefined;
  load = null;
  listeners = 0;
  waiters.splice(0, waiters.length);
}

/** One PUT at a time for `/oms/layouts/blotter/default`. */
export function queueFillLayoutSave(
  request: Request,
  patch: { columns?: OmsLayoutColumn[]; sort?: OmsLayoutSort | null; fillGroupBy?: OmsFillGroupBy },
): Promise<boolean> {
  draft = {
    columns: patch.columns ?? draft.columns,
    sort: patch.sort === undefined ? draft.sort : (patch.sort ?? undefined),
    fillGroupBy: patch.fillGroupBy ?? draft.fillGroupBy,
  };
  pending = true;
  lastRequest = request;
  return new Promise((resolve) => {
    waiters.push(resolve);
    if (hydrated) void pump(request);
  });
}

function finish(ok: boolean): void {
  const pendingWaiters = waiters.splice(0, waiters.length);
  for (const resolve of pendingWaiters) resolve(ok);
}

async function pump(request: Request): Promise<void> {
  if (sending) return;
  sending = true;
  conflicts = 0;
  let ok = true;
  while (pending) {
    pending = false;
    const snapshot: Draft = {
      columns: draft.columns.map((column) => ({ ...column })),
      sort: draft.sort ? { ...draft.sort } : undefined,
      fillGroupBy: draft.fillGroupBy,
    };
    const layout: OmsLayout = {
      tab: 'blotter',
      layoutName: OMS_COLUMN_LAYOUT_NAME,
      columns: snapshot.columns,
      ...(snapshot.sort ? { sort: snapshot.sort } : {}),
      ...(snapshot.fillGroupBy ? { fillGroupBy: snapshot.fillGroupBy } : {}),
      ...(version != null ? { version } : {}),
    };
    const result = await request<OmsLayout>(LAYOUT_PATH, {
      method: 'PUT',
      json: layout,
      requestSchema: omsLayoutSchema,
      responseSchema: omsLayoutSchema,
      toastError: false,
    });
    if (result.status === 200 && result.data) {
      version = typeof result.data.version === 'number' ? result.data.version : version;
      conflicts = 0;
      continue;
    }
    if ((result.status === 409 || result.status === 0) && conflicts < 2) {
      conflicts += 1;
      const listed = await request<OmsLayoutList>('/oms/layouts?tab=blotter', {
        responseSchema: omsLayoutListSchema,
        toastError: false,
        coalesce: 'url',
      });
      const stored = listed.status === 200 ? listed.data?.layouts.find((item) => item.layoutName === OMS_COLUMN_LAYOUT_NAME) : undefined;
      if (stored && typeof stored.version === 'number') version = stored.version;
      if (draft.columns.length === 0 && stored?.columns?.length) draft.columns = stored.columns;
      if (!pending) {
        draft = snapshot;
        pending = true;
      }
      continue;
    }
    if (!pending) ok = false;
  }
  sending = false;
  finish(ok);
}
